import { useCallback, useMemo, useState } from "react";

import { useInvoices } from "$/api/payment";
import { Button } from "$/lib/components";
import Pagination from "$/lib/components/molecules/Pagination/Pagination";
import DownloadIcon from "$/lib/icons/DownloadIcon";
import PaiementIcon from "$/lib/icons/PaiementIcon";
import { InvoiceStatusEnum } from "$/lib/types/models/Invoice.types";
import { cn } from "$/lib/utils/functions/misc.functions";

interface Props {
  className?: string;
}

const InvoicesList = ({ className }: Props) => {
  const [page, setPage] = useState(1);

  const { data: invoicesData, isLoading } = useInvoices({
    page,
  });

  const totalNumberOfPages = invoicesData?.totalNumberOfPages ?? 1;

  const invoices = useMemo(
    () =>
      invoicesData?.invoices.map((inv) => ({
        id: inv.number,
        date: new Date(inv.createdAt).toLocaleDateString("fr-FR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }),
        amount: `${(inv.amountPaid / 100).toFixed(2)} €`,
        status:
          inv.status === InvoiceStatusEnum.PAID
            ? "Payée"
            : inv.status === InvoiceStatusEnum.OPEN
              ? "En attente"
              : "Annulée",
        invoicePdfUrl: inv.invoicePdfUrl,
      })) || [],
    [invoicesData],
  );

  const handleDownloadInvoice = (url: string | null) => {
    if (!url) return;
    window.open(url, "_blank");
  };

  const handleDownloadAll = useCallback(() => {
    for (const invoice of invoicesData?.invoices || [])
      handleDownloadInvoice(invoice.invoicePdfUrl);
  }, [invoicesData]);

  const handleNextPage = () => {
    if (page < totalNumberOfPages) setPage((prev) => prev + 1);
  };

  const handlePreviousPage = () => {
    if (page > 1) setPage((prev) => prev - 1);
  };

  const handleSetPageNumber = (pageNumber: number) => {
    setPage(pageNumber);
  };

  return (
    <div
      className={cn(
        "flex flex-col self-stretch rounded-xl border border-border bg-white p-5",
        className,
      )}
    >
      {isLoading ? (
        <div className="flex grow items-center justify-center">
          <span className="text-sm text-gray-400">Chargement...</span>
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="mb-6 flex flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 screen850:h-12 screen850:w-12 items-center justify-center rounded-lg border border-border">
                <PaiementIcon className="size-6 text-[#1E1C2D]" />
              </div>
              <h2 className="text-[15px] screen850:text-base font-bold text-textColor">
                Mon historique de facturation
              </h2>
            </div>
            <Button
              onClick={handleDownloadAll}
              theme="primary"
              className="h-8 w-8 whitespace-nowrap p-0 screen850:h-auto screen850:w-auto screen850:px-4 screen850:py-2"
            >
              <DownloadIcon className="h-4 w-4 screen850:h-4 screen850:w-4" />
              <span className="hidden text-sm font-medium screen850:inline">
                Tout télécharger
              </span>
            </Button>
          </div>

          {/* Table */}
          <div className="grow overflow-x-auto rounded-xl border border-[#E8E8EA]">
            <table className="w-full min-w-[500px] table-fixed screen850:min-w-[600px]">
              <thead>
                <tr className="border-b border-[#E8E8EA]">
                  <th className="w-10 px-3 py-4 text-left screen850:hidden" />
                  <th className="px-6 py-4 text-left text-[13px] screen850:text-sm font-semibold text-[#696680]">
                    Numéro
                  </th>
                  <th className="px-6 py-4 text-left text-[13px] screen850:text-smfont-semibold text-[#696680]">
                    Date
                  </th>
                  <th className="px-6 py-4 text-left text-[13px] screen850:text-sm font-semibold text-[#696680]">
                    Montant
                  </th>
                  <th className="px-6 py-4 text-left text-[13px] screen850:text-sm font-semibold text-[#696680]">
                    Statut
                  </th>
                  <th className="hidden px-6 py-4 text-left text-sm font-semibold text-[#696680] screen850:table-cell">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice, index) => (
                  <tr
                    key={index}
                    className={`${index < invoices.length - 1 ? "border-b border-[#E8E8EA]" : ""} transition-colors hover:bg-primary-50/50`}
                  >
                    <td className="py-2 pl-3 pr-0 screen850:hidden">
                      <button
                        onClick={() =>
                          handleDownloadInvoice(invoice.invoicePdfUrl)
                        }
                        aria-label="Télécharger la facture"
                        className="inline-flex items-center text-[#1E1C2D] transition-colors hover:text-textInfo"
                      >
                        <DownloadIcon className="h-4 w-4" />
                      </button>
                    </td>
                    <td className="whitespace-nowrap px-6 py-2 text-sm font-semibold text-textColor">
                      {invoice.id}
                    </td>
                    <td className="px-6 py-2 text-sm font-semibold text-textColor">
                      {invoice.date}
                    </td>
                    <td className="px-6 py-2 text-sm font-semibold text-textColor">
                      {invoice.amount}
                    </td>
                    <td className="px-6 py-2">
                      <span className="inline-flex items-center gap-2 rounded-full bg-[#E6F9F4] px-3 py-1.5">
                        <span className="text-xs font-semibold text-[#00A67E]">
                          {invoice.status}
                        </span>
                        <span className="h-2 w-2 rounded-full bg-[#00A67E]" />
                      </span>
                    </td>
                    <td className="hidden py-2 pr-6 text-right screen850:table-cell">
                      <button
                        onClick={() =>
                          handleDownloadInvoice(invoice.invoicePdfUrl)
                        }
                        className="inline-flex items-center gap-2 text-sm font-semibold text-[#1E1C2D] transition-colors hover:text-textInfo"
                      >
                        <DownloadIcon className="h-4 w-4" />
                        Télécharger
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mb-1 mt-6">
            <Pagination
              currentPage={page}
              totalNumberOfPages={totalNumberOfPages}
              onNextClick={handleNextPage}
              onPreviousClick={handlePreviousPage}
              onSetPageNumber={handleSetPageNumber}
              isPendingData={isLoading}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default InvoicesList;
